<script>
const PartnerConfig = {
    ROUTES: {
        WELCOME: 'partners.default.index',
        COUPON: 'partners.default.coupon',
        COUPONVALIDATE: 'partners.default.coupon.validate',
        CHECKOUT: 'partners.default.checkout',
    }
};
</script>
<script setup>
import Auth from '../Auth.vue';
import DangerButton from '@/Components/DangerButton.vue';
import PartnerLayout from '@/Layouts/Partners/DefaultLayout.vue';
import Home from './Home.vue';
import IcoSpinner from '@/Images/IcoSpinner.vue';
import ImgValide from '@/Images/ImgValide.vue';
import InputError from '@/Components/InputError.vue';
import Subscription from './Subscription.vue';
import { Head } from '@inertiajs/vue3';
import { nextTick, onMounted, reactive, ref, toRef, watch } from 'vue';

import {
    childSubscription,
    childAuth,
    cpn,
    is_stripe_coupon,
    is_local_coupon,
    goToStep,
    getChildCheckout,
    getChildCoupon,
    last_subscription_is_processing,
    last_subscription_is,
    myOnMounted,
    initOnAuthStateChanged,
    partner_slug,
    resetCheckout,
    resetCoupon,
    step,
    updateBreadcrumb,
    subscription,
    ssc_default,
    ssc,
    ssp_default,
    ssp,
    user,
    validate_coupon_immediately
} from '../Welcome';
import { updateSubscription } from '../updateSubscription';

import { locale } from '@/Localization.js';
import { trans, transChoice } from 'laravel-vue-i18n';

const props = defineProps({
    debug: { type: Boolean, default: false },
    entitlement_id: { type: String },
    entitlement_lookup_key: { type: String },
    offering: { type: Object, },
    success_url: { type: String },
    cancel_url: { type: String },
    coupon: { type: Object },
    //
    _partner_slug: { type: String },
    _partner_name: { type: String },
    _layout: { type: Object },
    _step: { type: Number },
    show_social_connexion_default: { type: Boolean },
    show_social_connexion: { type: Boolean },
    show_success_page_default: { type: Boolean },
    show_success_page: { type: Boolean }, // false = validate Coupon/Stripe Immediately after authentication.
    coupon_from_qs: { type: String },
    has_local_coupon: { type: Boolean },
    has_stripe_coupon: { type: Boolean },
    show_stripe_offers: { type: Boolean },
    reset_cookie_cpn: { type: Boolean },
});

const debug = toRef(props.debug);
// debug.value = true; // Set to "true" to print debug. 

const offering = reactive(props.offering ? props.offering : {});

const show_social_connexion = toRef(props.show_social_connexion);
const show_success_page = toRef(props.show_success_page);
const coupon_from_qs = toRef(props.coupon_from_qs);
const has_local_coupon = toRef(props.has_local_coupon);
const has_stripe_coupon = toRef(props.has_stripe_coupon);
const show_stripe_offers = toRef(props.show_stripe_offers);
const reset_cookie_cpn = toRef(props.reset_cookie_cpn);

const route_welcome = PartnerConfig.ROUTES.WELCOME;
const route_coupon = PartnerConfig.ROUTES.COUPON;
const route_coupon_validate = PartnerConfig.ROUTES.COUPONVALIDATE;
const route_checkout = PartnerConfig.ROUTES.CHECKOUT;

partner_slug.value = props._partner_slug;
ssc_default.value = props.show_social_connexion_default;
ssc.value = show_social_connexion.value;
ssp_default.value = props.show_success_page_default;
ssp.value = show_success_page.value;
validate_coupon_immediately.value = !ssp.value;

is_local_coupon.value = has_local_coupon.value;
is_stripe_coupon.value = has_stripe_coupon.value;

cpn.value = coupon_from_qs.value;

const layout = ref(null);
layout.value = props._layout;

const reloadOffering = async (code, callback) => {

    // console.debug('Welcome.reloadOffering()', { code: code });

    const partner = route().current().split('.')[1];
    const url = route(`partners.${partner}.offering`, { 'locale': locale.value, 'partner_slug': partner_slug.value, cpn: code });

    return axios.get(url)
        .then(response => {
            if (response.data.packages) {
                offering.packages.items = response.data.packages.items;
                nextTick(() => {
                    getChildCheckout().init(true);
                });
            }

            return (callback)();
        });
}

const resetCpnCookie = () => {

    //  console.debug('Welcome.resetCpnCookie()', { app_user_coupon: getCookie('app_user_coupon') });

    resetCoupon();
    window.setCookie('app_user_coupon', null, -1);
    window.setCookie('app_user_coupon_scope', null, -1);
    reloadOffering(null, () => { })
}

if (reset_cookie_cpn.value) {
    resetCpnCookie()
}

watch(() => show_success_page.value,
    (val, oldVal) => {
        validate_coupon_immediately.value = !val;
    });

watch(() => has_stripe_coupon.value,
    (val, oldVal) => {
        is_stripe_coupon.value = val;
    });

onMounted(() => {

    // console.debug('Welcome.onMounted()', JSON.parse(JSON.stringify(subscription)));

    initOnAuthStateChanged()

    // L'état mémoire (subscription.app_user_coupon) est perdu à chaque rechargement
    // complet de la page (ex: retour du login social sur mobile) : on restaure le
    // coupon depuis le cookie de façon SYNCHRONE, pour que updateBreadcrumb() le
    // voie dès son premier calcul sans dépendre de la revalidation XHR.
    var comp = getChildCoupon();
    var coupon_from_cookie = window.getCookie('app_user_coupon');
    var coupon_restorable = comp
        && !coupon_from_qs.value
        && coupon_from_cookie
        && coupon_from_cookie != 'null'
        && window.getCookie('app_user_coupon_scope') == partner_slug.value;
    if (coupon_restorable) {
        comp.form.coupon = coupon_from_cookie;
        // Assignation directe : updateSubscription() déclencherait updateBreadcrumb()
        // avant l'initialisation de l'étape.
        subscription.app_user_coupon = coupon_from_cookie;
    }

    if (step.value === null) {
        // Coupon restauré = étape 1 acquise : on reprend à l'étape 2 (authentification)
        // au lieu du first_step de la config.
        goToStep(coupon_restorable ? 2 : props._step);
        myOnMounted();
    } else {
        if (typeof route().params.step != 'undefined') {
            goToStep(route().params.step);
        }
    }

    // Check & process coupon from QS.
    var comp = getChildCoupon();
    var coupon_from_cookie = window.getCookie('app_user_coupon');
    if (comp && coupon_from_qs.value) {
        comp.form.coupon = coupon_from_qs.value;

        if (has_local_coupon.value || has_stripe_coupon.value) {
            nextTick(() => {
                comp.preCheckCoupon();
            });
        } else {
            // show_stripe_offers? We need subscription.app_user_coupon_stripe (set by CouponForm.setAppUserCoupon())!
            comp.checkCoupon();
        }
    } else if (coupon_restorable) {
        // Revalidation asynchrone du coupon restauré : recalcule le label et re-signe
        // les cookies ; si le coupon est invalide, resetCoupon() ramènera à l'étape 1.

        if (has_local_coupon.value || has_stripe_coupon.value) {
            nextTick(() => {
                comp.preCheckCoupon();
            });
        } else {
            comp.checkCoupon();
        }
    }
});
</script>

<template>

    <Head :title="`${_partner_name} : Inscription`" />

    <Suspense>
        <PartnerLayout :layout="layout" :route_welcome="route_welcome" :partner_slug="partner_slug" :route_coupon="route_coupon" :route_checkout="route_checkout">

            <Transition>
                <fieldset v-if="step == 0" class="p-6 text-center">
                    <Home @goToStep="goToStep" :user="user" :subscription="subscription" :last_subscription_is_processing="last_subscription_is_processing" :last_subscription_is="last_subscription_is" :show_social_connexion="show_social_connexion" :show_stripe_offers="show_stripe_offers" />
                </fieldset>
            </Transition>

            <Transition>
                <fieldset v-show="step == 1 || (step == 5 && !validate_coupon_immediately.value)" class="p-6">
                    <legend v-if="layout && layout.show_step_number" class=" mx-auto dark:text-light font-bold">
                        {{ trans('Étape') }} 1/3
                    </legend>
                    <Subscription ref="childSubscription" :entitlement_lookup_key="entitlement_lookup_key" :entitlement_id="entitlement_id" :subscription="subscription" @updateSubscription="updateSubscription" @reloadOffering="reloadOffering" :offering="offering" :success_url="success_url" :cancel_url="cancel_url" :coupon="coupon" :route_checkout="route_checkout" :route_coupon="route_coupon" :route_coupon_validate="route_coupon_validate" :partner_slug="partner_slug" :layout="layout" :has_stripe_coupon="has_stripe_coupon" :has_local_coupon="has_local_coupon" :show_stripe_offers="show_stripe_offers" :debug="debug" />
                </fieldset>
            </Transition>

            <Transition>
                <fieldset v-show="step == 2" class="p-6 pt-0">
                    <legend v-if="layout && layout.show_step_number" class="mx-auto dark:text-light font-bold">
                        {{ trans('Étape') }} 2/3
                    </legend>
                    <!-- <h2 class="mb-6 text-center text-3xl font-bold">
                        {{ trans('Créer un compte ou connectez-vous') }}
                    </h2> -->

                    <div class="mb-6 group relative text-center">
                     <!--   <ImgValide v-if="layout" class="mx-auto mb-3" /> -->

                        <div class="font-bold text-lg mb-1">
                            <template v-if="childSubscription && childSubscription.childCoupon && childSubscription.childCoupon.currentCoupon.duration == 'lifetime'">
                                {{ trans('Votre code vous donne un accès à vie à MeditBe') }}
                            </template>
                            <template v-else>
                                <template v-if="subscription.label">
                                    {{ subscription.label }}
                                </template>
                                <span v-else class="text-gray-400">
                                    ...
                                </span>
                            </template>
                        </div>
<!--
                        <div>
                            {{ trans('Vous devez maintenant créer un compte pour activer votre offre') }}
                        </div>
-->
                        <!-- <a href="#" @click="resetCoupon(), resetCheckout(), updateBreadcrumb()"
                        class="mt-2 inline-flex underline text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-800">
                        {{ trans('Saisir un autre coupon') }}
                        </a> -->
                    </div>

                    <Auth ref="childAuth" :show_social_connexion="show_social_connexion" :layout="layout" />
                </fieldset>
            </Transition>

            <Transition>
                <fieldset v-show="step == 4" class="p-6 text-center">
                    <div v-if="childSubscription
        && childSubscription.childCheckout
        && childSubscription.childCheckout.form.hasErrors">
                        <h3 class="font-semibold text-lg text-gray-800 dark:text-gray-200 leading-tight">
                            Oups !
                        </h3>
                        <InputError class="mt-2" :message="childSubscription.childCheckout.form.errors.lookup_key" />
                        <InputError class="mt-2" :message="childSubscription.childCheckout.form.errors.app_user_id" />
                        <Transition>
                            <InputError v-if="subscription.app_user_id && childSubscription.childCheckout.form.errors.entitlement_already_subscribed" class="mt-2" :message="trans('Vous avez déjà un abonnement en cours.')" />
                        </Transition>
                        <div class="mt-6">
                            <a :href="route(route_welcome, { 'locale': locale, 'partner_slug': partner_slug })">
                                {{ trans('Retour') }}
                            </a>
                        </div>
                    </div>
                    <template v-else>
                        <h3 class="font-semibold text-lg text-gray-800 dark:text-gray-200 leading-tight">
                            {{ trans('Votre compte a été créé avec succès') }}
                        </h3>
                        <div class="mt-2">
                            <IcoSpinner />
                            {{ trans('Redirection en cours vers le site de paiement sécurisé de notre partenaire Stripe') }}
                        </div>
                    </template>
                </fieldset>
            </Transition>

            <div v-if="debug">
                <hr class="mt-10 mb-14 border-gray-300" />

                <DangerButton @click="resetCpnCookie()" class="mb-8">Détruire le cookie du coupon</DangerButton>

                <pre class="text-blue-700">step: {{ step }}</pre>
                <!-- <pre class="text-red-700">PartnerConfig: {{ PartnerConfig }}</pre> -->
                <!-- <pre class="text-red-700">layout: {{ layout }}</pre> -->
                <pre class="text-purple-700">subscription: {{ subscription }}</pre>
                <!-- <pre class="text-purple-700">props: {{ props }}</pre> -->
                <pre v-if="subscription.app_user_coupon_stripe" class="text-pink-700">subscription.app_user_coupon_stripe: {{ offering.promotional }}</pre>
                <pre class="text-yellow-700">offering: {{ offering }}</pre>

                <fieldset class="border border-solid border-gray-300 my-8 p-3 mb-8">
                    <legend class="text-sm text-gray-400">config:</legend>
                    <div>
                        <label class="pr-4">step:</label>
                        <input readonly disabled class="disabled:opacity-75 disabled:text-gray-700" v-model="step" placeholder="step" type="number" min="0" max="5" />
                    </div>
                    <div><label><input readonly disabled class="disabled:opacity-75 disabled:bg-gray-400" v-model="show_social_connexion" type="checkbox" /> show_social_connexion</label>
                    </div>
                    <div><label><input readonly disabled class="disabled:opacity-75 disabled:bg-gray-400" v-model="show_success_page" type="checkbox" /> show_success_page</label></div>
                    <div><label><input readonly disabled class="disabled:opacity-75 disabled:bg-gray-400" v-model="has_local_coupon" type="checkbox" /> has_local_coupon</label> (15MHXRKP,
                        premium)
                    </div>
                    <div><label><input readonly disabled class="disabled:opacity-75 disabled:bg-gray-400" v-model="has_stripe_coupon" type="checkbox" /> has_stripe_coupon</label> (MEDITBE20,
                        ZEROCDM, PM12RORO)
                    </div>
                    <div><label><input readonly disabled class="disabled:opacity-75 disabled:bg-gray-400" v-model="show_stripe_offers" type="checkbox" /> show_stripe_offers</label></div>
                </fieldset>

            </div>

        </PartnerLayout>
    </Suspense>
</template>
